import { buildChatListBulkEmailBatchPayload } from './chatListState';

const BULK_EMAIL_CHANNEL_TYPE = 'Channel::Email';
const BULK_EMAIL_INBOX_PROVIDER_GOOGLE = 'google';
const BULK_EMAIL_INBOX_PROVIDER_MICROSOFT = 'microsoft';
const BULK_EMAIL_PROVIDER_GMAIL = 'gmail';
const BULK_EMAIL_PROVIDER_MICROSOFT = 'microsoft';
const BULK_EMAIL_PROVIDER_IMAP = 'imap';
const BULK_EMAIL_ARCHIVE_FAILED_MESSAGE_KEY =
  'BULK_ACTION.EMAIL.ARCHIVE_FAILED';
const BULK_EMAIL_ARCHIVE_SUCCESS_MESSAGE_KEY =
  'BULK_ACTION.EMAIL.ARCHIVE_SUCCESS';
const BULK_EMAIL_ARCHIVED_STATUS = 'archived';
const BULK_EMAIL_BATCH_KIND_ARCHIVE = 'archive';
const BULK_EMAIL_BATCH_KIND_TRASH = 'trash';
const BULK_EMAIL_MOVE_FAILED_MESSAGE_KEY = 'BULK_ACTION.EMAIL.MOVE_FAILED';
const BULK_EMAIL_TRASH_FAILED_MESSAGE_KEY = 'BULK_ACTION.EMAIL.TRASH_FAILED';
const BULK_EMAIL_TRASH_SUCCESS_MESSAGE_KEY = 'BULK_ACTION.EMAIL.TRASH_SUCCESS';
const BULK_EMAIL_TRASHED_STATUS = 'trashed';

const getEmailProvider = (inbox) => {
  if (!inbox || inbox.channel_type !== BULK_EMAIL_CHANNEL_TYPE) {
    return null;
  }

  if (inbox.provider === BULK_EMAIL_INBOX_PROVIDER_GOOGLE) {
    return BULK_EMAIL_PROVIDER_GMAIL;
  }

  if (inbox.provider === BULK_EMAIL_INBOX_PROVIDER_MICROSOFT) {
    return BULK_EMAIL_PROVIDER_MICROSOFT;
  }

  return BULK_EMAIL_PROVIDER_IMAP;
};

const buildBulkEmailActionContext = ({
  conversations = null,
  conversationIds = [],
  getConversationById,
  getInbox,
}) => {
  const ids = Array.isArray(conversationIds) ? conversationIds : [];
  if (!ids.length) {
    return null;
  }

  const resolvedConversations = Array.isArray(conversations)
    ? conversations
    : ids.map((id) => getConversationById(id));
  if (
    resolvedConversations.length !== ids.length ||
    resolvedConversations.some((conversation) => !conversation)
  ) {
    return null;
  }

  const inboxId = resolvedConversations[0].inbox_id;
  if (
    !inboxId ||
    resolvedConversations.some(
      (conversation) => Number(conversation.inbox_id) !== Number(inboxId)
    )
  ) {
    return null;
  }

  const inbox = getInbox(inboxId);
  const provider = getEmailProvider(inbox);
  if (!provider) {
    return null;
  }

  return {
    conversations: resolvedConversations,
    firstConv: resolvedConversations[0],
    inbox,
    inboxId,
    provider,
  };
};

const runBulkEmailBatchAction = ({
  action = null,
  buildPayload = buildChatListBulkEmailBatchPayload,
  conversationIds = [],
  dispatch = null,
  inboxId = null,
} = {}) => {
  if (typeof dispatch !== 'function') {
    return null;
  }
  return dispatch(action, buildPayload({ conversationIds, inboxId }));
};

const runBulkEmailMoveBatchAction = ({
  action = null,
  conversationIds = [],
  currentFolderId = null,
  dispatch = null,
  inboxId = null,
  targetFolderId = null,
  targetFolderTitle = null,
} = {}) => {
  if (typeof dispatch !== 'function') {
    return null;
  }
  return dispatch(action, {
    conversationIds,
    currentFolderId,
    inboxId,
    targetFolderId,
    targetFolderTitle,
  });
};

const resolveBulkGmailMoveContext = ({
  currentFolderId = null,
  isCurrentFolderTarget = false,
  isSearchModeActive = false,
} = {}) => ({
  // Provider search results can come from any Gmail label while the route
  // still carries the folder that opened the search (usually INBOX). Treating
  // that stale route label as the source either skips the request entirely or
  // asks Gmail to add and remove INBOX in the same operation.
  currentFolderId: isSearchModeActive ? null : currentFolderId,
  shouldSkip: !isSearchModeActive && Boolean(isCurrentFolderTarget),
});

const buildBulkEmailBatchRequest = ({
  buildContext = null,
  failureMessageKey = null,
  selectedConversationIds = [],
} = {}) => {
  const conversationIds = Array.isArray(selectedConversationIds)
    ? [...selectedConversationIds]
    : [];
  if (!conversationIds.length) {
    return {
      context: null,
      conversationIds,
      shouldReset: false,
      shouldSkip: true,
    };
  }

  const context =
    typeof buildContext === 'function'
      ? buildContext(conversationIds, failureMessageKey)
      : null;
  return {
    context,
    conversationIds,
    shouldReset: !context,
    shouldSkip: !context,
  };
};

const prepareBulkEmailBatchRequest = ({
  buildRequest = null,
  failureMessageKey = null,
  reset = null,
} = {}) => {
  const request =
    typeof buildRequest === 'function'
      ? buildRequest(failureMessageKey)
      : buildBulkEmailBatchRequest({ failureMessageKey });
  if (!request.shouldSkip) {
    return request;
  }
  if (request.shouldReset && typeof reset === 'function') {
    reset();
  }
  return null;
};

const applyBulkEmailBatchSuccessEffect = ({
  clearCache = null,
  conversationIds = [],
  emitCompleted = null,
  inboxId = null,
  status = null,
  successMessageKey = null,
} = {}) => {
  if (typeof emitCompleted === 'function') {
    emitCompleted(conversationIds, status);
  }
  if (typeof clearCache === 'function') {
    clearCache({ inboxId });
  }
  return {
    clearCachePayload: { inboxId },
    conversationIds,
    status,
    successMessageKey,
  };
};

const buildBulkEmailBatchSuccessOptions = ({
  archiveSuccessKey = BULK_EMAIL_ARCHIVE_SUCCESS_MESSAGE_KEY,
  archiveStatus = BULK_EMAIL_ARCHIVED_STATUS,
  kind = null,
  trashSuccessKey = BULK_EMAIL_TRASH_SUCCESS_MESSAGE_KEY,
  trashStatus = BULK_EMAIL_TRASHED_STATUS,
} = {}) => {
  if (kind === BULK_EMAIL_BATCH_KIND_ARCHIVE) {
    return { status: archiveStatus, successMessageKey: archiveSuccessKey };
  }
  return { status: trashStatus, successMessageKey: trashSuccessKey };
};

export {
  applyBulkEmailBatchSuccessEffect,
  buildBulkEmailBatchRequest,
  buildBulkEmailBatchSuccessOptions,
  buildBulkEmailActionContext,
  BULK_EMAIL_ARCHIVE_FAILED_MESSAGE_KEY,
  BULK_EMAIL_ARCHIVE_SUCCESS_MESSAGE_KEY,
  BULK_EMAIL_ARCHIVED_STATUS,
  BULK_EMAIL_BATCH_KIND_ARCHIVE,
  BULK_EMAIL_BATCH_KIND_TRASH,
  BULK_EMAIL_CHANNEL_TYPE,
  BULK_EMAIL_INBOX_PROVIDER_GOOGLE,
  BULK_EMAIL_INBOX_PROVIDER_MICROSOFT,
  BULK_EMAIL_MOVE_FAILED_MESSAGE_KEY,
  BULK_EMAIL_PROVIDER_GMAIL,
  BULK_EMAIL_PROVIDER_IMAP,
  BULK_EMAIL_PROVIDER_MICROSOFT,
  BULK_EMAIL_TRASH_FAILED_MESSAGE_KEY,
  BULK_EMAIL_TRASH_SUCCESS_MESSAGE_KEY,
  BULK_EMAIL_TRASHED_STATUS,
  getEmailProvider,
  prepareBulkEmailBatchRequest,
  resolveBulkGmailMoveContext,
  runBulkEmailBatchAction,
  runBulkEmailMoveBatchAction,
};
